import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { ToDoBundleContext } from './context';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { TypeSource } from './NewTodo';
import { buildTypeSource } from './todoBusinessUtils';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';
import { todoStatus } from './state';
import { useSetAtom } from 'jotai';
import { queries } from 'services/queries';
import { useQuery } from '@tanstack/react-query';

type ContextType = {
  task?: Todo.Task;
  taskId: number | undefined;
  typeSource: TypeSource;
  refresh: () => void;
  loading: boolean;
};

export const TypeSourceContext = createContext<ContextType>({} as ContextType);

export const TypeSourceProvider = (props: { children: ReactNode }) => {
  const { storeId } = useContext(ToDoBundleContext);
  const { t } = useTranslate();
  const { getTypeLabel } = useTypeLabel();
  const setStatus = useSetAtom(todoStatus);

  const {
    data,
    isLoading: loading,
    refetch: refresh,
    error,
  } = useQuery({
    ...queries.tickets.task(storeId),
    enabled: !!storeId,
  });

  const task = data?.task;

  useEffect(() => {
    if (error) {
      message.error(t('common.feedback.fetchFailed'));
    }
  }, [error, t]);

  useEffect(() => {
    if (!task) {
      setStatus('3');
    }
  }, [setStatus, task]);

  const typeSource = useMemo<TypeSource>(
    () => buildTypeSource(data?.policies, getTypeLabel as (type: Todo.Type) => string | number),
    [data?.policies, getTypeLabel],
  );

  return (
    <TypeSourceContext.Provider
      value={{
        task,
        taskId: task?.id,
        typeSource,
        refresh: refresh,
        loading,
      }}
    >
      {props.children}
    </TypeSourceContext.Provider>
  );
};
